// Admin API client utility
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8147/api';
export const ADMIN_UNAUTHORIZED_EVENT = 'spacejet-admin:unauthorized';

const SESSION_REVOKED_CODES = new Set([
  'ACCOUNT_NOT_APPROVED',
  'ACCOUNT_REJECTED',
  'ACCOUNT_INACTIVE',
  'ACCOUNT_NOT_FOUND'
]);

const shouldClearAdminSession = (status, code) => (
  status === 401 || (status === 403 && SESSION_REVOKED_CODES.has(code))
);

const notifyAdminSessionRejected = ({ endpoint, status, message }) => {
  const hasSession = Boolean(localStorage.getItem('spacejet-admin-token'));

  if (hasSession && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ADMIN_UNAUTHORIZED_EVENT, {
      detail: {
        endpoint,
        status,
        message
      }
    }));
  }
};

class AdminApiClient {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const timeoutMs = Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : 0;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutHandle = timeoutMs && controller
      ? setTimeout(() => controller.abort(new Error(`Request timed out after ${timeoutMs}ms`)), timeoutMs)
      : null;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller?.signal,
      ...options,
    };

    delete config.timeoutMs;

    // Add admin auth token if available
    const token = localStorage.getItem('spacejet-admin-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const makeRequest = async (attempt = 0) => {
        const response = await fetch(url, config);
        if (response.status === 429 && attempt < 2) {
          const retryAfter = Number.parseInt(response.headers.get('retry-after') || '', 10);
          const backoffMs = Number.isFinite(retryAfter) && retryAfter > 0
            ? retryAfter * 1000
            : (500 * (attempt + 1));
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          return makeRequest(attempt + 1);
        }
        return response;
      };

      const response = await makeRequest();
      const responseText = await response.text();
      let data = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = { message: responseText || null };
      }

      if (!response.ok) {
        if (shouldClearAdminSession(response.status, data?.code)) {
          notifyAdminSessionRejected({
            endpoint,
            status: response.status,
            message: data?.message || 'Invalid, expired, or revoked token'
          });
        }

        const requestError = new Error(data.message || `HTTP error! status: ${response.status}`);
        requestError.code = data.code;
        requestError.status = response.status;
        requestError.details = data.errors;
        requestError.data = data;
        throw requestError;
      }

      return data;
    } catch (error) {
      if (error?.name === 'AbortError') {
        const timeoutError = new Error(timeoutMs ? `Request timed out after ${timeoutMs}ms` : 'Request was aborted');
        timeoutError.name = 'AbortError';
        throw timeoutError;
      }
      console.error('Admin API request failed:', error);
      throw error;
    } finally {
      if (timeoutHandle) clearTimeout(timeoutHandle);
    }
  }

  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }
}

// Create and export a default instance
const adminApi = new AdminApiClient();

// Specific API methods for admin
export const adminAuthAPI = {
  login: (credentials) => adminApi.post('/auth/admin-login', credentials),
  verify: (token) => adminApi.post('/auth/verify', { token }),
};

export const adminContactsAPI = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/contacts${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => adminApi.get(`/contacts/${id}`),
  updateStatus: (id, status) => adminApi.put(`/contacts/${id}`, { status }),
  delete: (id) => adminApi.delete(`/contacts/${id}`),
};

export const adminUsersAPI = {
  create: (payload) => adminApi.post('/users', payload),
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/users${queryString ? `?${queryString}` : ''}`);
  },
  overlap: () => adminApi.get('/users/overlap'),
  overlapCategories: () => adminApi.get('/users/overlap/categories'),
  createOverlapCategory: (name) => adminApi.post('/users/overlap/categories', { name }),
  deleteOverlapCategory: (id) => adminApi.delete(`/users/overlap/categories/${id}`),
  createIntroduction: (payload) => adminApi.post('/users/overlap/introductions', payload),
  updateIntroduction: (id, payload) => adminApi.patch(`/users/overlap/introductions/${id}`, payload),
  signupSubmissions: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/users/signup-submissions${queryString ? `?${queryString}` : ''}`);
  },
  enrollSignupSubmission: (id) => adminApi.post(`/users/signup-submissions/${id}/enroll`, {}),
  getById: (id) => adminApi.get(`/users/${id}`),
  update: (id, userData) => adminApi.put(`/users/${id}`, userData),
  delete: (id) => adminApi.delete(`/users/${id}`),
  accessRequests: () => adminApi.get('/users/access-requests'),
  reviewAccessRequest: (accountType, id, status) => adminApi.put(`/users/access-requests/${accountType}/${id}`, { status }),
};

export const adminDiscoveryAPI = {
  leads: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/leads${queryString ? `?${queryString}` : ''}`);
  },
  listSdrOwners: () => adminApi.get('/discovery/sdr-owners'),
  getLeadById: (id) => adminApi.get(`/discovery/leads/${id}`),
  getLeadWorkspace: (id) => adminApi.get(`/discovery/leads/${id}/workspace`),
  listLeadEvents: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/leads/${id}/events${queryString ? `?${queryString}` : ''}`);
  },
  getLeadSdrDetails: (id) => adminApi.get(`/discovery/leads/${id}/sdr-details`),
  updateLeadSdrDetails: (id, payload) => adminApi.put(`/discovery/leads/${id}/sdr-details`, payload),
  getLeadSdrAgent: (id) => adminApi.get(`/discovery/leads/${id}/sdr-agent`),
  updateLeadSdrAgentConfig: (id, payload) => adminApi.put(`/discovery/leads/${id}/sdr-agent/config`, payload),
  createLeadSdrAgentTask: (id, payload) => adminApi.post(`/discovery/leads/${id}/sdr-agent/tasks`, payload),
  updateLeadSdrAgentTask: (id, taskId, payload) => adminApi.patch(`/discovery/leads/${id}/sdr-agent/tasks/${taskId}`, payload),
  addLeadSdrAgentTaskLog: (id, taskId, payload) => adminApi.post(`/discovery/leads/${id}/sdr-agent/tasks/${taskId}/logs`, payload),
  updateLeadWorkspace: (id, payload) => adminApi.put(`/discovery/leads/${id}/workspace`, payload),
  getLeadSdrState: (id) => adminApi.get(`/discovery/leads/${id}/sdr-state`),
  updateLeadSdrState: (id, payload) => adminApi.put(`/discovery/leads/${id}/sdr-state`, payload),
  handoffLeadToHubSpot: (id, payload = {}) => adminApi.post(`/discovery/leads/${id}/hubspot-handoff`, payload, { timeoutMs: 15000 }),
  resetHubspotHandoffState: (payload = {}) => adminApi.post('/discovery/hubspot/reset', payload, { timeoutMs: 20000 }),
  reviewLead: (id, payload) => adminApi.patch(`/discovery/leads/${id}/review`, payload),
  bulkReview: (payload) => adminApi.post('/discovery/leads/bulk-review', payload),
  bulkSdrDetails: (payload) => adminApi.post('/discovery/leads/bulk-sdr-details', payload),
  sources: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/sources${queryString ? `?${queryString}` : ''}`);
  },
  createSource: (payload) => adminApi.post('/discovery/sources', payload),
  updateSource: (id, payload) => adminApi.patch(`/discovery/sources/${id}`, payload),
  runs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/runs${queryString ? `?${queryString}` : ''}`);
  },
  runById: (id) => adminApi.get(`/discovery/runs/${id}`),
  triggerRun: (payload = {}) => adminApi.post('/discovery/runs/trigger', payload),
  triggerAutomationRun: (payload = {}) => adminApi.post('/discovery/runs/automate', payload),
  automationStatus: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/automation/status${queryString ? `?${queryString}` : ''}`);
  },
  discoverSources: (payload = {}) => adminApi.post('/discovery/sources/discover', payload),
  overview: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/metrics/overview${queryString ? `?${queryString}` : ''}`);
  },
  sourceHealth: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/discovery/metrics/source-health${queryString ? `?${queryString}` : ''}`);
  },
  getLeadEvidence: (id) => adminApi.get(`/discovery/leads/${id}/evidence`)
};

export const adminReportingAPI = {
  listAdvertiserApis: () => adminApi.get('/reporting/advertiser-apis'),
  createAdvertiserApi: (payload) => adminApi.post('/reporting/advertiser-apis', payload),
  updateAdvertiserApi: (id, payload) => adminApi.patch(`/reporting/advertiser-apis/${id}`, payload),
  runAdvertiserApi: (id) => adminApi.post(`/reporting/advertiser-apis/${id}/run`, {}),
  listAdvertiserApiRuns: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/advertiser-apis/${id}/runs${queryString ? `?${queryString}` : ''}`);
  },
  listAdvertiserApiRecords: (id, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/advertiser-apis/${id}/records${queryString ? `?${queryString}` : ''}`);
  },
  advertiserAutomationStatus: () => adminApi.get('/reporting/advertiser-apis/automation/status'),
  runAdvertiserAutomation: () => adminApi.post('/reporting/advertiser-apis/automation/run', {}),
  signupsReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/reports/signups${queryString ? `?${queryString}` : ''}`);
  },
  categoriesReport: () => adminApi.get('/reporting/reports/categories'),
  reportingOverview: () => adminApi.get('/reporting/reports/overview'),
  pixelPerformanceReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/reports/pixel-performance${queryString ? `?${queryString}` : ''}`);
  },
  advertiserApiStatsReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/reports/advertiser-api-stats${queryString ? `?${queryString}` : ''}`);
  },
  reportRuns: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/report-runs${queryString ? `?${queryString}` : ''}`);
  },
  listExternalConnections: () => adminApi.get('/reporting/external-connections'),
  createExternalConnection: (payload) => adminApi.post('/reporting/external-connections', payload),
  updateExternalConnection: (id, payload) => adminApi.patch(`/reporting/external-connections/${id}`, payload),
  listCustomReports: () => adminApi.get('/reporting/custom-reports'),
  createCustomReport: (payload) => adminApi.post('/reporting/custom-reports', payload),
  updateCustomReport: (id, payload) => adminApi.patch(`/reporting/custom-reports/${id}`, payload),
  runCustomReport: (id) => adminApi.post(`/reporting/custom-reports/${id}/run`, {}),
  exportCustomReport: (id, format = 'json') => adminApi.get(`/reporting/custom-reports/${id}/export?format=${encodeURIComponent(format)}`),
  runAdHocReport: (payload) => adminApi.post('/reporting/custom-reports/run-ad-hoc', payload),
  reportScheduleStatus: () => adminApi.get('/reporting/custom-reports/schedules'),
  runReportSchedules: () => adminApi.post('/reporting/custom-reports/schedules/trigger', {}),
  createReportSchedule: (payload) => adminApi.post('/reporting/custom-reports/schedules', payload),
  updateReportSchedule: (id, payload) => adminApi.patch(`/reporting/custom-reports/schedules/${id}`, payload),
  importLinkTrustCampaigns: (payload) => adminApi.post('/reporting/linktrust/campaign-import', payload),
  listLinkTrustMerchantOptions: () => adminApi.get('/reporting/linktrust/merchant-options'),
  syncLinkTrustAffiliates: (payload) => adminApi.post('/reporting/linktrust/affiliates/sync', payload),
  syncLinkTrustAdvertisers: (payload) => adminApi.post('/reporting/linktrust/advertisers/sync', payload),
  syncLinkTrustCampaigns: (payload) => adminApi.post('/reporting/linktrust/campaigns/sync', payload),
  syncLinkTrustCreatives: (payload) => adminApi.post('/reporting/linktrust/creatives/sync', payload),
  syncLinkTrustTransactionsByCampaign: (payload) => adminApi.post('/reporting/linktrust/reports/transactions-by-campaign/sync', payload),
  syncLinkTrustClickListing: (payload) => adminApi.post('/reporting/linktrust/reports/clicklisting/sync', payload),
  startLinkTrustClickListingJob: (payload) => adminApi.post('/reporting/linktrust/reports/clicklisting/jobs/start', payload),
  getLinkTrustClickListingJob: (runId) => adminApi.get(`/reporting/linktrust/reports/clicklisting/jobs/${runId}`),
  listLinkTrustClickListingJobs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/reports/clicklisting/jobs${queryString ? `?${queryString}` : ''}`);
  },
  controlLinkTrustClickListingJob: (runId, action) => adminApi.post(`/reporting/linktrust/reports/clicklisting/jobs/${runId}/control`, { action }),
  retryFailedLinkTrustClickListingJob: (runId, payload) => adminApi.post(`/reporting/linktrust/reports/clicklisting/jobs/${runId}/retry-failures`, payload),
  resumeLinkTrustClickListingJobFromCheckpoint: (runId, payload) => adminApi.post(`/reporting/linktrust/reports/clicklisting/jobs/${runId}/resume-from-checkpoint`, payload),
  listLinkTrustMappingProfiles: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/mapping-profiles${queryString ? `?${queryString}` : ''}`);
  },
  saveLinkTrustMappingProfile: (payload) => adminApi.post('/reporting/linktrust/mapping-profiles', payload),
  deleteLinkTrustMappingProfile: (id) => adminApi.delete(`/reporting/linktrust/mapping-profiles/${id}`),
  listLinkTrustAutomationProfiles: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/automation-profiles${queryString ? `?${queryString}` : ''}`);
  },
  getLinkTrustAutomationProfile: (id) => adminApi.get(`/reporting/linktrust/automation-profiles/${id}`),
  saveLinkTrustAutomationProfile: (payload) => adminApi.post('/reporting/linktrust/automation-profiles', payload),
  updateLinkTrustAutomationProfileContext: (id, payload) => adminApi.put(`/reporting/linktrust/automation-profiles/${id}/context`, payload),
  deleteLinkTrustAutomationProfile: (id) => adminApi.delete(`/reporting/linktrust/automation-profiles/${id}`),
  syncLinkTrustAffiliateLeadStatus: (payload) => adminApi.post('/reporting/linktrust/reports/affiliate-lead-status/sync', payload),
  listLinkTrustAdvertisers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/advertisers${queryString ? `?${queryString}` : ''}`);
  },
  rebuildLinkTrustAdvertisersFromCampaigns: (payload = {}) => adminApi.post('/reporting/linktrust/advertisers/rebuild-from-campaigns', payload),
  getLinkTrustAdvertiserSettings: (merchantId) => adminApi.get(`/reporting/linktrust/advertisers/${merchantId}/settings`),
  listLinkTrustCampaignManagement: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/campaign-management${queryString ? `?${queryString}` : ''}`);
  },
  backfillLinkTrustCampaignAdvertisers: (payload) => adminApi.post('/reporting/linktrust/campaigns/backfill-advertisers', payload),
  bulkDeleteLinkTrustCampaigns: (payload) => adminApi.post('/reporting/linktrust/campaigns/bulk-delete', payload),
  bulkUpdateLinkTrustCampaignStatuses: (payload) => adminApi.put('/reporting/linktrust/campaign-import/status', payload),
  pushLinkTrustCampaignStatuses: (payload) => adminApi.put('/reporting/linktrust/campaigns/status/push', payload),
  updateLinkTrustCampaignDetails: (campaignId, payload) => adminApi.put(`/reporting/linktrust/campaigns/${campaignId}/update`, payload),
  updateLinkTrustAdvertiserManagement: (merchantId, payload) => adminApi.put(`/reporting/linktrust/advertisers/${merchantId}/management`, payload),
  updateLinkTrustAffiliateManagement: (merchantId, affiliateId, payload) => adminApi.put(`/reporting/linktrust/affiliates/${merchantId}/${affiliateId}/management`, payload),
  updateLinkTrustOutboundQueueStatus: (id, payload) => adminApi.patch(`/reporting/linktrust/outbound-updates/${id}/status`, payload),
  listLinkTrustAffiliates: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/affiliates${queryString ? `?${queryString}` : ''}`);
  },
  listLinkTrustCreatives: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/creatives${queryString ? `?${queryString}` : ''}`);
  },
  generateLinkTrustLinks: (payload) => adminApi.post('/reporting/linktrust/links/generate', payload),
  listLinkTrustLinks: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/links${queryString ? `?${queryString}` : ''}`);
  },
  listLinkTrustCampaigns: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return adminApi.get(`/reporting/linktrust/campaigns${queryString ? `?${queryString}` : ''}`);
  },
  importLinkTrustCreatives: (payload) => adminApi.post('/reporting/linktrust/creative-import', payload),
  uploadLinkTrustCreativeAssets: async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const token = localStorage.getItem('spacejet-admin-token');
    const response = await fetch(`${API_BASE_URL}/reporting/linktrust/creative-assets/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      if (shouldClearAdminSession(response.status, data?.code)) {
        notifyAdminSessionRejected({
          endpoint: '/reporting/linktrust/creative-assets/upload',
          status: response.status,
          message: data?.message || 'Invalid, expired, or revoked token'
        });
      }

      const requestError = new Error(data.message || `HTTP error! status: ${response.status}`);
      requestError.code = data.code;
      requestError.status = response.status;
      requestError.details = data.errors;
      requestError.data = data;
      throw requestError;
    }
    return data;
  }
};

export default adminApi;
